/* eslint-env es2020 */
// zpost-app/src/components/ensapp/WhoIsWho.js
import React, { useState, useEffect, useCallback } from 'react';
import { namehash } from 'ethers';
import { getRPCProvider } from '../../utils/rpc';  // <-- use shared RPC utility

const WhoIsWho = ({ identityAuth }) => {
  const [domainInput, setDomainInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainInfo, setDomainInfo] = useState(null);
  const [profileRecords, setProfileRecords] = useState(null);
  const [resolverInfo, setResolverInfo] = useState(null);
  const [currentUserDomain, setCurrentUserDomain] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [provider, setProvider] = useState(null);
  const [providerReady, setProviderReady] = useState(false);

  const validNameRegex = /^(?!.*\s)(?!.*\[)(?!.*\]).+\..+$/;

  // Initialize stable provider on mount
  useEffect(() => {
    let cancelled = false;
    const initProvider = async () => {
      try {
        const p = await getRPCProvider();  // uses the shared RPC endpoints
        if (!cancelled) {
          setProvider(p);
          setProviderReady(true);
        }
      } catch (err) {
        console.error('Failed to initialize RPC provider:', err);
        setError('Could not connect to Ethereum network. Please refresh.');
      }
    };
    initProvider();
    return () => { cancelled = true; };
  }, []);

  // Track current user's domain
  useEffect(() => {
    if (identityAuth?.ensName) {
      setCurrentUserDomain(identityAuth.ensName);
    }
  }, [identityAuth]);

  // Process avatar URL
  const processAvatarUrl = useCallback((avatarRecord, domain) => {
    if (!avatarRecord || avatarRecord.trim() === '') {
      return '';
    }

    // Handle IPFS URLs
    if (avatarRecord.startsWith('ipfs://')) {
      const ipfsHash = avatarRecord.replace('ipfs://', '');
      // Try multiple IPFS gateways for better reliability
      const gateways = [
        `https://ipfs.io/ipfs/${ipfsHash}`,
        `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`,
        `https://gateway.pinata.cloud/ipfs/${ipfsHash}`,
        `https://dweb.link/ipfs/${ipfsHash}`
      ];
      return gateways[0]; // Use first gateway, but we'll handle errors
    }

    // Handle HTTP/HTTPS URLs directly
    if (avatarRecord.startsWith('http://') || avatarRecord.startsWith('https://')) {
      return avatarRecord;
    }

    // Handle ENS metadata service URLs
    if (avatarRecord.includes('eip155:')) {
      // This is an NFT avatar - we can use the ENS metadata service
      return `https://metadata.ens.domains/mainnet/avatar/${domain}`;
    }

    return '';
  }, []);

  // Clean up domain input
  const normalizeDomain = (domain) => {
    if (!domain) return '';
    let clean = domain.trim().toLowerCase();
    clean = clean.replace(/^(https?:\/\/)?(www\.)?/, '');
    clean = clean.replace(/\/$/, '');
    return clean;
  };

  // Fetch domain information
  const lookupDomain = useCallback(async (domain) => {
    if (!provider) {
      setError('Ethereum network not ready. Please wait or refresh.');
      return;
    }
    if (!domain || !validNameRegex.test(domain)) {
      setError('Please enter a valid ENS domain (e.g., vitalik.eth)');
      return;
    }

    setIsLoading(true);
    setError('');
    setDomainInfo(null);
    setProfileRecords(null);
    setResolverInfo(null);
    setAvatarUrl('');
    setAvatarError(false);

    try {
      console.log(`🔍 Looking up domain: ${domain}`);

      // 1. Resolve domain to address
      const address = await provider.resolveName(domain);
      if (!address) {
        throw new Error(`Domain ${domain} does not resolve to any address`);
      }

      // 2. Get node hash
      const node = namehash(domain);
      
      // 3. Get resolver info
      let resolver = null;
      let resolverAddress = null;
      
      try {
        resolver = await provider.getResolver(domain);
        if (resolver) {
          resolverAddress = resolver.address;
        }
      } catch (resolverError) {
        console.warn('Could not get resolver via provider:', resolverError);
      }

      // 4. Check reverse resolution (primary name)
      let reverseName = null;
      try {
        reverseName = await provider.lookupAddress(address);
      } catch (e) {
        console.warn('Reverse lookup failed:', e);
      }

      // 5. Get all text records
      const textRecords = {};
      const textKeys = [
        'description', 'url', 'com.twitter', 'com.github', 
        'org.telegram', 'com.discord', 'location', 'email',
        'avatar', 'notice', 'keywords', 'com.reddit',
        'com.linkedin', 'io.keybase', 'org.ensdiscord'
      ];

      let avatarRecord = '';

      if (resolver) {
        const recordPromises = textKeys.map(async (key) => {
          try {
            const value = await resolver.getText(key);
            if (value && value.trim() !== '') {
              // Store avatar record separately
              if (key === 'avatar') {
                avatarRecord = value;
              }
              return { key, value };
            }
          } catch (e) {
            // Ignore errors for optional fields
          }
          return null;
        });

        const results = await Promise.all(recordPromises);
        results.forEach(result => {
          if (result) {
            textRecords[result.key] = result.value;
          }
        });

        // Process avatar URL
        if (avatarRecord) {
          const processedUrl = processAvatarUrl(avatarRecord, domain);
          setAvatarUrl(processedUrl);
          console.log('🖼️ Avatar URL:', processedUrl);
        }
      }

      // 6. Get content hash if available
      let contentHash = null;
      if (resolver) {
        try {
          contentHash = await resolver.getContentHash();
        } catch (e) {
          console.warn('Could not get content hash:', e);
        }
      }

      // 7. Get coin addresses
      const coinRecords = {};
      if (resolver) {
        const coinTypes = {
          'BTC': '0',
          'LTC': '2',
          'DOGE': '3',
          'ETH': '60',
          'USDT': '825'
        };

        const coinPromises = Object.entries(coinTypes).map(async ([coin, type]) => {
          try {
            const addr = await resolver.getAddress(parseInt(type));
            if (addr && addr !== '0x0000000000000000000000000000000000000000') {
              return { coin, address: addr };
            }
          } catch (e) {
            // Ignore
          }
          return null;
        });

        const coinResults = await Promise.all(coinPromises);
        coinResults.forEach(result => {
          if (result) {
            coinRecords[result.coin] = result.address;
          }
        });
      }

      // 8. Compile all domain info
      const domainData = {
        domain,
        address,
        reverseName,
        resolverAddress,
        registrationDate: null,
        expiryDate: null,
        node,
        contentHash,
        coinRecords,
        textRecords,
        isCurrentUser: identityAuth?.walletAddress && 
                      address.toLowerCase() === identityAuth.walletAddress.toLowerCase()
      };

      setDomainInfo(domainData);
      setProfileRecords(textRecords);
      setResolverInfo({
        address: resolverAddress,
        hasResolver: !!resolverAddress && resolverAddress !== '0x0000000000000000000000000000000000000000'
      });

    } catch (err) {
      console.error('Domain lookup failed:', err);
      setError(`Failed to lookup domain: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [provider, identityAuth, processAvatarUrl]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const normalized = normalizeDomain(domainInput);
    if (normalized) {
      lookupDomain(normalized);
    }
  };

  // Quick lookup helper
  const quickLookup = (domain) => {
    setDomainInput(domain);
    setTimeout(() => lookupDomain(domain), 50);
  };

  // Format address for display
  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Get social icon
  const getSocialIcon = (key) => {
    const icons = {
      'com.twitter': '🐦',
      'com.github': '🐙',
      'com.discord': '💬',
      'org.telegram': '✈️',
      'url': '🌐',
      'email': '📧',
      'location': '📍',
      'com.reddit': '👽',
      'com.linkedin': '💼',
      'avatar': '🖼️',
      'description': '📝'
    };
    return icons[key] || '📝';
  };

  // Save domain to contacts
  const saveToContacts = () => {
    if (!domainInfo) return;
    
    const contact = {
      domain: domainInfo.domain,
      address: domainInfo.address,
      records: profileRecords || {},
      avatarUrl,
      timestamp: new Date().toISOString()
    };
    
    const existingContacts = JSON.parse(localStorage.getItem('ens-contacts') || '[]');
    
    if (existingContacts.some(c => c.domain === domainInfo.domain)) {
      alert(`${domainInfo.domain} is already in your contacts!`);
      return;
    }
    
    existingContacts.push(contact);
    localStorage.setItem('ens-contacts', JSON.stringify(existingContacts));
    
    alert(`✅ Saved ${domainInfo.domain} to your contacts!`);
  };

  // Share profile
  const shareProfile = () => {
    if (!domainInfo) return;
    
    const shareText = `Check out ${domainInfo.domain} on ZPost Identity App!`;
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: domainInfo.domain,
        text: shareText,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('📤 Share link copied to clipboard!');
    }
  };

  // Try alternative IPFS gateways if one fails
  const handleAvatarError = () => {
    if (!avatarUrl || avatarError) return;
    
    console.log('🔄 Avatar failed to load, trying alternative gateways...');
    setAvatarError(true);
    
    // Extract IPFS hash from current URL
    if (avatarUrl.includes('ipfs.io/ipfs/')) {
      const ipfsHash = avatarUrl.split('ipfs.io/ipfs/')[1];
      const alternativeGateways = [
        `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`,
        `https://gateway.pinata.cloud/ipfs/${ipfsHash}`,
        `https://dweb.link/ipfs/${ipfsHash}`,
        `https://ipfs.infura.io/ipfs/${ipfsHash}`
      ];
      
      // Try first alternative
      if (alternativeGateways[0]) {
        console.log('🔄 Trying alternative gateway:', alternativeGateways[0]);
        setAvatarUrl(alternativeGateways[0]);
        setAvatarError(false); // Reset error for next try
      }
    }
  };

  if (!providerReady) {
    return (
      <div className="who-is-who content-card">
        <h3>🔍 Who Is Who - ENS Lookup</h3>
        <div className="identity-card" style={{ textAlign: 'center', padding: '3rem', background: 'var(--black)', border: '1px solid var(--gold)' }}>
          <p style={{ color: 'var(--gold)' }}>Connecting to Ethereum network...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="who-is-who content-card">
      <h3>🔍 Who Is Who - ENS Lookup</h3>
      <p className="text-secondary">Look up any ENS domain to see its profile information, social links, and connected addresses</p>

      {/* Search Form */}
      <div className="search-section" style={{ marginBottom: '2rem' }}>
        <form onSubmit={handleSubmit} className="domain-input-group">
          <input
            type="text"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="Enter ENS domain (e.g., tom.zpost.eth, vitalik.eth)"
            className="domain-input"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-gold"
            style={{ minWidth: '120px' }}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Looking up...
              </>
            ) : 'Lookup'}
          </button>
        </form>

        {/* Quick Lookup - Only show "My Domain" button if user has one */}
        {currentUserDomain && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <div className="btn-group" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => quickLookup(currentUserDomain)}
                className="btn btn-gold"
                style={{ fontSize: '0.9rem' }}
              >
                👤 My Domain: {currentUserDomain}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="status-message status-error">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: 'var(--gold)', fontSize: '2rem', marginBottom: '1rem' }}>🔍</div>
          <p style={{ color: 'var(--gold)' }}>Querying Ethereum network for domain information...</p>
          <p className="text-secondary">
            Fetching address, resolver, and profile records
          </p>
        </div>
      )}

      {/* Domain Information Display */}
      {domainInfo && !isLoading && (
        <div className="content-card" style={{ marginTop: '2rem' }}>
          {/* Avatar Display Section - Similar to AvatarManager */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '2rem',
            padding: '2rem',
            background: 'rgba(255, 215, 0, 0.05)',
            borderRadius: '12px',
            border: '2px dashed var(--gold)'
          }}>
            {avatarUrl ? (
              <>
                <img
                  src={avatarUrl}
                  alt={`${domainInfo.domain} avatar`}
                  style={{
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid var(--gold)',
                    marginBottom: '1rem'
                  }}
                  onError={handleAvatarError}
                />
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Avatar for {domainInfo.domain}
                </p>
                {profileRecords?.avatar && (
                  <p style={{ 
                    color: 'var(--gold)', 
                    fontSize: '0.8rem', 
                    marginTop: '0.5rem',
                    wordBreak: 'break-all',
                    textAlign: 'center'
                  }}>
                    Source: {profileRecords.avatar}
                  </p>
                )}
              </>
            ) : (
              <>
                <div style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'rgba(255, 215, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed var(--gold)',
                  marginBottom: '1rem'
                }}>
                  <span style={{ color: 'var(--gold)', fontSize: '48px' }}>👤</span>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  No avatar set for {domainInfo.domain}
                </p>
              </>
            )}
          </div>

          {/* Check if this is the current user's domain */}
          {domainInfo.isCurrentUser && (
            <div className="status-message" style={{ 
              background: 'rgba(255, 215, 0, 0.1)', 
              borderLeftColor: 'var(--gold)',
              color: 'var(--gold)',
              marginBottom: '1rem'
            }}>
              ✅ This is YOUR domain! Switch to the "Profile Records" or "Avatar" tab to manage it.
            </div>
          )}

          {/* Domain Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '2rem',
            borderBottom: '1px solid var(--border)',
            paddingBottom: '1rem'
          }}>
            <div>
              <h4 style={{ margin: 0, color: 'var(--gold)', fontSize: '1.2rem' }}>
                {domainInfo.domain}
              </h4>
              <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-secondary)' }}>
                {domainInfo.reverseName === domainInfo.domain ? '✓ Primary Name' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(domainInfo.domain)}
              className="btn"
              style={{
                fontSize: '0.9rem',
                background: 'transparent',
                color: 'var(--gold)',
                border: '1px solid var(--gold)'
              }}
            >
              Copy Domain
            </button>
          </div>

          {/* Address Section */}
          <div className="form-group" style={{ marginBottom: '2rem' }}>
            <label className="form-label">Resolved Address</label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'var(--black)',
              padding: '12px',
              borderRadius: '5px',
              border: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>↘️</span>
              <code style={{ 
                flex: 1, 
                color: 'var(--gold)',
                fontSize: '0.9rem',
                wordBreak: 'break-all'
              }}>
                {domainInfo.address}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(domainInfo.address)}
                className="btn"
                style={{
                  fontSize: '0.8rem',
                  background: 'transparent',
                  color: 'var(--gold)',
                  border: '1px solid var(--gold)'
                }}
              >
                Copy
              </button>
            </div>
            {domainInfo.reverseName && domainInfo.reverseName !== domainInfo.domain && (
              <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Primary name: <strong>{domainInfo.reverseName}</strong>
              </p>
            )}
          </div>

          {/* Resolver Info */}
          <div className="form-group" style={{ marginBottom: '2rem' }}>
            <label className="form-label">Resolver</label>
            <div className="status-message" style={{
              background: resolverInfo.hasResolver ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
              borderLeftColor: resolverInfo.hasResolver ? '#00ff88' : '#ff4444',
              color: resolverInfo.hasResolver ? '#00ff88' : '#ff4444'
            }}>
              {resolverInfo.hasResolver ? (
                <>✅ Has resolver: {formatAddress(resolverInfo.address)}</>
              ) : (
                <>❌ No resolver configured</>
              )}
            </div>
          </div>

          {/* Profile Records (excluding avatar since we already displayed it) */}
          {profileRecords && Object.keys(profileRecords).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Profile Records</h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '1rem'
              }}>
                {Object.entries(profileRecords)
                  .filter(([key]) => key !== 'avatar') // Exclude avatar since we already displayed it
                  .map(([key, value]) => (
                  <div 
                    key={key}
                    className="content-card"
                    style={{
                      border: '1px solid var(--border)',
                      padding: '1rem',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>{getSocialIcon(key)}</span>
                      <strong style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>
                        {key.replace(/^com\.|^org\./, '')}
                      </strong>
                    </div>
                    <p style={{ 
                      margin: 0, 
                      color: 'var(--text-light)',
                      fontSize: '0.9rem',
                      wordBreak: 'break-word'
                    }}>
                      {value}
                    </p>
                    {(key === 'url' || key === 'email') && (
                      <button
                        type="button"
                        onClick={() => window.open(key === 'url' ? value : `mailto:${value}`, '_blank')}
                        className="btn"
                        style={{
                          marginTop: '0.5rem',
                          fontSize: '0.8rem',
                          background: 'transparent',
                          color: 'var(--gold)',
                          border: '1px solid var(--gold)',
                          alignSelf: 'flex-start'
                        }}
                      >
                        {key === 'url' ? 'Visit' : 'Email'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coin Addresses */}
          {domainInfo.coinRecords && Object.keys(domainInfo.coinRecords).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Multi-Coin Addresses</h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {Object.entries(domainInfo.coinRecords).map(([coin, address]) => (
                  <div 
                    key={coin}
                    className="content-card"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>
                        {coin === 'BTC' ? '₿' : coin === 'ETH' ? 'Ξ' : '💎'}
                      </span>
                      <strong style={{ color: 'var(--gold)' }}>{coin}</strong>
                    </div>
                    <code style={{ 
                      color: 'var(--text-light)',
                      fontSize: '0.8rem',
                      wordBreak: 'break-all'
                    }}>
                      {address}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Hash */}
          {domainInfo.contentHash && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Content Hash</h4>
              <code className="content-card" style={{
                display: 'block',
                border: '1px solid var(--border)',
                padding: '12px',
                color: 'var(--text-light)',
                fontSize: '0.9rem',
                wordBreak: 'break-all'
              }}>
                {domainInfo.contentHash}
              </code>
            </div>
          )}

          {/* No Records Message */}
          {(!profileRecords || Object.keys(profileRecords).length === 0) && 
           (!domainInfo.coinRecords || Object.keys(domainInfo.coinRecords).length === 0) && (
            <div className="content-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              <p>No profile records found for this domain.</p>
              <p style={{ fontSize: '0.9rem' }}>
                The owner hasn't set up any social links or profile information yet.
              </p>
            </div>
          )}

          {/* Feature-Rich Action Buttons */}
          <div className="btn-group" style={{ 
            marginTop: '2rem',
            borderTop: '1px solid var(--border)',
            paddingTop: '1rem'
          }}>
            <button
              type="button"
              onClick={() => {
                setDomainInput('');
                setDomainInfo(null);
                setProfileRecords(null);
                setError('');
                setAvatarUrl('');
                setAvatarError(false);
              }}
              className="btn"
              style={{
                background: 'transparent',
                color: 'var(--gold)',
                border: '1px solid var(--gold)'
              }}
            >
              🔍 Lookup Another
            </button>

            <button
              type="button"
              onClick={saveToContacts}
              className="btn"
              style={{
                background: 'transparent',
                color: 'var(--gold)',
                border: '1px solid var(--gold)'
              }}
            >
              💾 Save Contact
            </button>

            <button
              type="button"
              onClick={shareProfile}
              className="btn"
              style={{
                background: 'transparent',
                color: 'var(--gold)',
                border: '1px solid var(--gold)'
              }}
            >
              📤 Share
            </button>

            <button
              type="button"
              onClick={() => window.open(`https://etherscan.io/address/${domainInfo.address}`, '_blank')}
              className="btn"
              style={{
                background: 'transparent',
                color: 'var(--gold)',
                border: '1px solid var(--gold)'
              }}
            >
              🔗 Etherscan
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!domainInfo && !isLoading && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <h4 style={{ color: 'var(--gold)' }}>Explore ENS Profiles</h4>
          <p>Enter any ENS domain above to see:</p>
          <ul style={{ 
            textAlign: 'left', 
            display: 'inline-block', 
            margin: '1rem auto',
            color: 'var(--text-secondary)'
          }}>
            <li>🖼️ <strong>Avatar</strong> - Profile picture</li>
            <li>🔗 Resolved Ethereum address</li>
            <li>🐦 Social media links & profiles</li>
            <li>🌐 Website and contact information</li>
            <li>₿ Multi-chain addresses (BTC, LTC, etc.)</li>
            <li>📦 Content hash and resolver info</li>
          </ul>
          <p className="text-secondary">
            All ENS domain information is public on the Ethereum blockchain
          </p>
        </div>
      )}
    </div>
  );
};

export default WhoIsWho;
